/**
 * Validation for the details a person types about themselves.
 *
 * Shared so the public apply form and the recruiter's resume review enforce the
 * same rules. A candidate applying from a careers page and a recruiter fixing a
 * parsed resume are entering the same fields; they should not disagree about
 * what counts as a phone number.
 *
 * These run in the browser for immediate feedback. They are not the guard —
 * the server checks the same things, because a form is only a suggestion.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// What a phone number may contain: digits and the punctuation people write them
// with. Checked BEFORE counting digits — stripping non-digits first would
// quietly accept "23y4iy234y298349" as a valid number.
const PHONE_ALLOWED_RE = /^\+?[\d\s().-]+$/;

export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 15;   // E.164 allows at most 15
export const MAX_EXPERIENCE_YEARS = 60;

/** Drop anything that cannot belong in a phone number, as it is typed. */
export function sanitizePhone(value) {
  const cleaned = String(value ?? '').replace(/[^+\d\s().-]/g, '');
  const lead = cleaned.startsWith('+') ? '+' : '';
  const rest = cleaned.replace(/\+/g, '');

  let digits = 0;
  let out = '';
  for (const ch of rest) {
    if (ch >= '0' && ch <= '9') {
      if (digits >= PHONE_MAX_DIGITS) break;
      digits += 1;
    }
    out += ch;
  }
  return lead + out;
}

/** Years of experience is a number. Keep digits and a single decimal point. */
export function sanitizeExperience(value) {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  return rest.length ? `${whole.slice(0, 2)}.${rest.join('').slice(0, 1)}` : whole.slice(0, 2);
}

export function phoneError(value, { required = false } = {}) {
  const phone = String(value || '').trim();
  if (!phone) return required ? 'Phone number is required' : null;
  if (!PHONE_ALLOWED_RE.test(phone)) return 'Digits only — letters are not allowed';
  if (phone.lastIndexOf('+') > 0) return 'A + may only appear at the start';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < PHONE_MIN_DIGITS) return `Too short — ${digits.length} digit${digits.length === 1 ? '' : 's'}`;
  if (digits.length > PHONE_MAX_DIGITS) return `Too long — ${digits.length} digits, maximum is ${PHONE_MAX_DIGITS}`;
  return null;
}

export function emailError(value, { required = false } = {}) {
  const email = String(value || '').trim();
  if (!email) return required ? 'Email is required' : null;
  if (!EMAIL_RE.test(email)) return 'Enter a valid email address, like jane@example.com';
  if (email.length > 200) return 'Email is too long';
  return null;
}

/**
 * A name has to contain a letter. "87686876786876786786" is not a name, and
 * letting it through means a recruiter opens a candidate they cannot address.
 */
export function nameError(value, { required = true } = {}) {
  const name = String(value || '').trim();
  if (!name) return required ? 'Name is required' : null;
  if (!/\p{L}/u.test(name)) return 'Enter a real name — digits alone are not a name';
  if (name.length > 200) return 'Name is too long';
  return null;
}

export function experienceError(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;                       // optional
  if (!/^\d{1,2}(\.\d)?$/.test(raw)) return 'Enter a number, like 5 or 7.5';
  const n = Number(raw);
  if (Number.isNaN(n)) return 'Enter a number, like 5 or 7.5';
  if (n > MAX_EXPERIENCE_YEARS) return `That looks too high — maximum ${MAX_EXPERIENCE_YEARS} years`;
  return null;
}
